import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getOfflineMap, saveOfflineMap } from '../utils/offlineDb';

export default function OfflineCategoryRenameModal({
  mapId,
  oldName,
  newName: initialNewName,
  connectedTypes,
  currentTypeId,
  initialTypesToMove = [],
  onConfirm,
  onCancel,
  updateCategories,          // for updating categories state in parent
  updateTypes,               // for updating types state in parent
  updateVisibleCategories,   // NEW: to add newly created category to visibleCategories set
  closeAllModals,            // NEW: to close ALL modals incl EditPOI modal
}) {
  const [categoryName, setCategoryName] = useState(initialNewName || oldName || '');
  const [typesToMove, setTypesToMove] = useState(() => {
    if (initialTypesToMove.length > 0) return new Set(initialTypesToMove);
    return new Set(connectedTypes.map(t => t.id));
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCategoryName(initialNewName || oldName || '');
  }, [initialNewName, oldName]);

  const toggleType = (typeId) => {
    setTypesToMove(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeId)) newSet.delete(typeId);
      else newSet.add(typeId);
      return newSet;
    });
  };

  const handleSimpleRename = async () => {
    if (!categoryName.trim()) {
      alert('Category name cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const bundle = await getOfflineMap(mapId);
      if (!bundle) throw new Error(`Map ${mapId} not found`);

      const categories = bundle.poi_categories || [];
      const originalCategory = categories.find(c => c.name.toLowerCase() === oldName.toLowerCase());
      const trimmedNewName = categoryName.trim();

      if (originalCategory) {
        originalCategory.name = trimmedNewName;
      } else {
        categories.push({
          id: Date.now(),
          map_id: mapId,
          name: trimmedNewName,
        });
      }

      bundle.poi_categories = categories;
      bundle.lastEdited = Date.now();

      await saveOfflineMap(mapId, bundle);

      if (updateCategories) updateCategories(categories);

      setSaving(false);

      // Just close this modal, go back to previous modal
      onConfirm(originalCategory ? originalCategory.id : null);
      window.dispatchEvent(new CustomEvent('offlineMapDataChanged', { detail: { mapId } }));
    } catch (error) {
      setSaving(false);
      alert('Failed to save category changes: ' + error.message);
      console.error(error);
    }
  };

  const handleRenameAndMove = async () => {
    if (!categoryName.trim()) {
      alert('Category name cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const bundle = await getOfflineMap(mapId);
      if (!bundle) throw new Error(`Map ${mapId} not found`);

      const categories = bundle.poi_categories || [];
      const types = bundle.poi_types || [];

      const trimmedNewName = categoryName.trim();

      let existingCategory = categories.find(c => c.name.toLowerCase() === trimmedNewName.toLowerCase());

      let newCategoryId;
      if (existingCategory) {
        newCategoryId = existingCategory.id;
      } else {
        newCategoryId = Date.now();
        categories.push({
          id: newCategoryId,
          map_id: mapId,
          name: trimmedNewName,
        });
      }

      types.forEach(type => {
        if (typesToMove.has(type.id)) {
          type.category_id = newCategoryId;
        }
      });

      bundle.poi_categories = categories;
      bundle.poi_types = types;
      bundle.lastEdited = Date.now();

      await saveOfflineMap(mapId, bundle);

      if (updateCategories) updateCategories(categories);
      if (updateTypes) updateTypes(types);

      // Ensure the new category is visible immediately
      if (updateVisibleCategories) {
        updateVisibleCategories(oldSet => {
          const next = new Set(oldSet);
          next.add(newCategoryId);
          return next;
        });
      }

      setSaving(false);

      // Close ALL modals, including EditPOI modal
      if (closeAllModals) closeAllModals();

      onConfirm(newCategoryId, typesToMove);
      window.dispatchEvent(new CustomEvent('offlineMapDataChanged', { detail: { mapId } }));
    } catch (error) {
      setSaving(false);
      alert('Failed to rename and move category: ' + error.message);
      console.error(error);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        zIndex: 15000,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'inherit',
      }}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          backgroundColor: '#232427',
          padding: 24,
          borderRadius: 12,
          minWidth: 320,
          maxWidth: '90vw',
          color: '#ffe089',
          boxShadow: '0 0 10px #000',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>Rename Category</h2>

        <label htmlFor="category-name-input" style={{ display: 'block', marginBottom: 8 }}>
          New Category Name:
        </label>
        <input
          id="category-name-input"
          type="text"
          value={categoryName}
          onChange={e => setCategoryName(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #555',
            marginBottom: 12,
            fontSize: 16,
            color: '#232427',
          }}
          autoFocus
          disabled={saving}
        />

        <button
          onClick={handleSimpleRename}
          disabled={saving || !categoryName.trim()}
          style={{
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: 8,
            fontWeight: 'bold',
            cursor: saving || !categoryName.trim() ? 'not-allowed' : 'pointer',
            width: '100%',
            marginBottom: 20,
          }}
        >
          {saving ? 'Saving...' : 'Simple Rename'}
        </button>

        <hr style={{ borderColor: '#555', marginBottom: 20 }} />

        <div style={{ marginBottom: 12 }}>
          <p style={{ marginBottom: 12 }}>
            Or add as a new category and move selected types from the current category:
          </p>

          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 16 }}>
            {connectedTypes.length === 0 ? (
              <p style={{ color: '#bbb', fontStyle: 'italic' }}>No types connected to this category.</p>
            ) : (
              connectedTypes.map(type => {
                const isCurrent = type.id === currentTypeId;
                return (
                  <label
                    key={type.id}
                    style={{
                      display: 'block',
                      marginBottom: 8,
                      cursor: 'pointer',
                      backgroundColor: isCurrent ? 'rgba(76, 193, 83, 0.25)' : 'transparent',
                      fontWeight: isCurrent ? 'bold' : 'normal',
                      padding: '4px 8px',
                      borderRadius: 4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={typesToMove.has(type.id)}
                      onChange={() => toggleType(type.id)}
                      style={{ marginRight: 8 }}
                      disabled={saving}
                    />
                    {type.name} {isCurrent && '(Currently Editing)'}
                  </label>
                );
              })
            )}
          </div>

          <button
            onClick={handleRenameAndMove}
            disabled={saving || !categoryName.trim()}
            style={{
              backgroundColor: '#4cc153',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: 8,
              fontWeight: 'bold',
              cursor: saving || !categoryName.trim() ? 'not-allowed' : 'pointer',
              width: '100%',
            }}
          >
            {saving ? 'Saving...' : 'Rename & Move Selected Types'}
          </button>
        </div>

        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            marginTop: 12,
            backgroundColor: '#f44c2b',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: 8,
            fontWeight: 'bold',
            cursor: saving ? 'not-allowed' : 'pointer',
            width: '100%',
          }}
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body
  );
}
